import React, { useEffect, useRef, useState } from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import { useT } from '../../i18n/LanguageContext';
import { translations } from '../../i18n/translations';

/**
 * SectionDualTimeline — visual explainer for "Das Dual System".
 *
 * Two parallel 12-month timelines running side by side:
 *   • CLIENT → 1 diamond bought, briefly cut, held untouched for 12 months, delivered end
 *   • STIG   → same 12 months but 4 buy/polish/sell CYCLES rotate through
 *
 * All CSS-only animation to stay smooth on projection setups.
 */
export default function SectionDualTimeline() {
  const ref = useRef(null);
  const shellRef = useRef(null);
  const [play, setPlay] = useState(false);
  const [showStig, setShowStig] = useState(false);
  const { lang } = useT();
  const T = translations[lang]?.dualTimeline || translations.de.dualTimeline;

  // Extra labels not covered by translations.js dualTimeline block yet.
  const EXTRA_LABELS = {
    de: { scale: 'Timeline Monate', idAssign: 'ID Zuordnung', giaAssign: 'Zuordnung mit GIA Nummer' },
    en: { scale: 'Timeline (months)', idAssign: 'ID Assignment', giaAssign: 'Assignment with GIA number' },
    tr: { scale: 'Zaman Çizelgesi (ay)', idAssign: 'Kimlik Ataması', giaAssign: 'GIA Numarası ile Atama' },
    hu: { scale: 'Idővonal (hónap)', idAssign: 'Azonosító hozzárendelés', giaAssign: 'Hozzárendelés GIA számmal' },
    fr: { scale: 'Chronologie (mois)', idAssign: 'Attribution d\u2019ID', giaAssign: 'Attribution avec numéro GIA' },
    es: { scale: 'Cronología (meses)', idAssign: 'Asignación de ID', giaAssign: 'Asignación con número GIA' },
    it: { scale: 'Cronologia (mesi)', idAssign: 'Assegnazione ID', giaAssign: 'Assegnazione con numero GIA' },
    pt: { scale: 'Cronologia (meses)', idAssign: 'Atribuição de ID', giaAssign: 'Atribuição com número GIA' },
    nl: { scale: 'Tijdlijn (maanden)', idAssign: 'ID-toewijzing', giaAssign: 'Toewijzing met GIA-nummer' },
    ru: { scale: 'Хронология (месяцы)', idAssign: 'Присвоение ID', giaAssign: 'Привязка к номеру GIA' },
    pl: { scale: 'Oś czasu (miesiące)', idAssign: 'Przypisanie ID', giaAssign: 'Przypisanie z numerem GIA' },
  };
  const LBL = EXTRA_LABELS[lang] || EXTRA_LABELS.en;

  // 4 steps that make up ONE cycle, with matching thumbnail images to keep
  // the visual language consistent across the presentation.
  const CYCLE_STEPS = [
    { label: T.stig.cycleSteps[0], img: '/images/dual/01_customer_purchase.png' },
    { label: T.stig.cycleSteps[1], img: '/images/dual/02_dual_assignment.png' },
    { label: T.stig.cycleSteps[2], img: '/images/dual/03_value_creation.png' },
    { label: T.stig.cycleSteps[3], img: '/images/dual/04_active_trading.png' },
  ];

  useEffect(() => {
    const node = ref.current;
    if (!node || play) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.35) {
            setPlay(true);
            obs.disconnect();
          }
        });
      },
      { threshold: [0.35, 0.5, 0.75] }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [play]);

  useEffect(() => {
    const node = shellRef.current;
    if (!node) return undefined;
    const onFragment = () => setShowStig(true);
    node.addEventListener('fragment-advance', onFragment);
    return () => node.removeEventListener('fragment-advance', onFragment);
  }, []);

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  // Nur 1, 2, 3 … und 12 beschriften (User-Wunsch)
  const labelledMonths = [1, 2, 3, 12];
  // Nicht-lineare Skala: Monate 1-3 gespreizt (je 20 % Breite), damit die
  // Ereignis-Punkte der Kunden-Timeline weiter auseinander liegen und
  // größer beschriftet werden können. Monate 3-12 teilen sich die restlichen 40 %.
  const monthPos = (m) => (m <= 3 ? m * 20 : 60 + ((m - 3) / 9) * 40);

  return (
    <SectionShell id="dual-timeline" centerContent>
      <div
        ref={(el) => { ref.current = el; shellRef.current = el; }}
        className={`dt-shell ${play ? 'dt-play' : ''} ${showStig ? 'dt-stig-revealed' : ''}`}
        data-testid="dual-timeline"
        data-fragments-remaining={showStig ? 0 : 1}
      >
        <Reveal>
          <div className="overline mb-4" style={{ color: 'rgba(242,217,121,0.9)' }}>
            {T.overline}
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-12 md:mb-16 text-white">
            {T.title}
          </h2>
          <p className="font-ui text-base md:text-lg text-white/75 leading-relaxed mb-12 max-w-[68ch]">
            {T.intro.split('\n').map((line, i, arr) => (
              <React.Fragment key={i}>
                {line}
                {i < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        </Reveal>

        {/* Month scale — mirrors the lane grid so ticks align with the tracks below.
            Ticks positioned by absolute left = (month/12) * 100% inside the scale-track,
            matching the same coordinate system used by the timeline event markers. */}
        <div className="dt-scale" aria-hidden>
          <div className="dt-scale-head">
            <span className="dt-scale-head-label">{LBL.scale}</span>
          </div>
          <div className="dt-scale-track">
            <div className="dt-scale-fill" />
            {months.map((m) => (
              <div
                key={m}
                className="dt-tick"
                style={{ left: `${monthPos(m)}%` }}
              >
                {labelledMonths.includes(m) && <span className="dt-tick-label">{m}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* ─── CLIENT TIMELINE ─── */}
        <div className="dt-lane dt-lane-customer">
          <div className="dt-lane-head">
            <div className="dt-lane-kicker">{T.customer.kicker}</div>
            <div className="dt-lane-title" style={{ whiteSpace: 'pre-line' }}>{T.customer.title}</div>
            <div className="dt-lane-sub" style={{ whiteSpace: 'pre-line' }}>{T.customer.sub}</div>
          </div>
          <div className="dt-track dt-track-customer">
            {/* Held-bar background */}
            <div className="dt-track-fill dt-track-fill-customer" />
            {/* Purchase marker (month 0) */}
            <div className="dt-event dt-event-purchase" style={{ left: '0%' }}>
              <div className="dt-event-dot" />
              <div className="dt-event-icon">◆</div>
              <div className="dt-event-label">{T.customer.purchase}</div>
            </div>
            {/* ID assignment (right after purchase, month 0.5) — label ABOVE */}
            <div className="dt-event dt-event-idassign dt-event--above" style={{ left: `${monthPos(0.5)}%` }}>
              <div className="dt-event-dot" />
              <div className="dt-event-icon">#</div>
              <div className="dt-event-label">{LBL.idAssign}</div>
            </div>
            {/* Cut marker (month 1) */}
            <div className="dt-event dt-event-cut" style={{ left: `${monthPos(1)}%` }}>
              <div className="dt-event-dot" />
              <div className="dt-event-icon">✦</div>
              <div className="dt-event-label">{T.customer.cut}</div>
            </div>
            {/* Certification marker (month 2) — label ABOVE */}
            <div className="dt-event dt-event-certify dt-event--above" style={{ left: `${monthPos(2)}%` }}>
              <div className="dt-event-dot" />
              <div className="dt-event-icon">✓</div>
              <div className="dt-event-label">{T.customer.certify}</div>
            </div>
            {/* GIA-Number assignment marker (month 3) — dark green */}
            <div
              className="dt-event dt-event-giaassign"
              style={{ left: `${monthPos(3)}%` }}
            >
              <div className="dt-event-dot" />
              <div className="dt-event-icon">✓</div>
              <div className="dt-event-label">{LBL.giaAssign}</div>
            </div>
            {/* Hold-glow bar between certification and delivery */}
            <div className="dt-hold" />
            {/* Delivery marker (month 12) */}
            <div className="dt-event dt-event-delivery" style={{ left: '100%' }}>
              <div className="dt-event-dot" />
              <div className="dt-event-icon">▲</div>
              <div className="dt-event-label">{T.customer.delivery}</div>
            </div>
          </div>
        </div>

        {/* ─── STIG ROCK TIMELINE — erscheint erst nach separatem Klick ─── */}
        <div className={`dt-lane dt-lane-stig ${showStig ? 'dt-lane-stig--visible' : 'dt-lane-stig--hidden'}`} data-testid="dt-stig-lane">
          <div className="dt-lane-head">
            <div className="dt-lane-kicker" style={{ color: 'rgba(242,217,121,0.9)' }}>{T.stig.kicker}</div>
            <div
              className="dt-lane-title dt-lane-title--stig"
              style={{ whiteSpace: 'pre-line' }}
            >
              STIG{'\n'}INTERNATIONAL
            </div>
            <div className="dt-lane-sub">
              {String(T.stig.sub).split('·').map((s, i) => (
                <span key={i} className="block">{s.trim()}</span>
              ))}
            </div>
          </div>
          {/* STIG bar removed per user request — the 4 rotating cycle nodes
              below carry the full visual weight of the STIG timeline. */}

          {/* 4 CYCLE NODES — circular loop-icons carry the STIG timeline visual */}
          <div className="dt-cycle-nodes" data-testid="dt-cycle-nodes">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`dt-cycle-node ${i === 0 ? 'dt-cycle-node--primary' : 'dt-cycle-node--secondary'}`}
                style={{ animationDelay: `${i * 240}ms` }}
              >
                <div className="dt-cycle-connector dt-cycle-connector--hidden" aria-hidden />
                <div className="dt-cycle-circle" aria-hidden>
                  <svg viewBox="0 0 48 48" className="dt-cycle-loop">
                    <defs>
                      <marker
                        id={`dt-arrow-${i}`}
                        viewBox="0 0 8 8"
                        refX="4"
                        refY="4"
                        markerWidth="4"
                        markerHeight="4"
                        orient="auto"
                      >
                        <path d="M0,0 L8,4 L0,8 z" fill="#f2d979" />
                      </marker>
                    </defs>
                    <path
                      d="M 40 24 A 16 16 0 1 1 24 8"
                      fill="none"
                      stroke="rgba(242,217,121,0.9)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      markerEnd={`url(#dt-arrow-${i})`}
                    />
                  </svg>
                  <div className="dt-cycle-circle-num">{({ de: 'Z', tr: 'D' }[lang] || 'C')}{i + 1}</div>
                </div>
                <div className="dt-cycle-circle-caption">
                  {CYCLE_STEPS.map((step, si) => (
                    <React.Fragment key={step.label}>
                      <div className="dt-cycle-step-item" title={step.label}>
                        <div className="dt-cycle-step-thumb">
                          <img src={step.img} alt="" loading="lazy" />
                        </div>
                        <span className="dt-cycle-step-lbl">{step.label}</span>
                      </div>
                      {si < CYCLE_STEPS.length - 1 && (
                        <span className="dt-cycle-arrow" aria-hidden>→</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="dt-lane-footer">
            <span className="dt-cycle-badge">{T.stig.footerCycle}</span>
            <span className="dt-cycle-badge dt-cycle-badge-gold">{T.stig.footerDiscount}</span>
          </div>
        </div>

        {/* Bottom summary strip — erscheint erst mit dem STIG-Fragment-Klick */}
        <div className={`dt-summary ${showStig ? '' : 'dt-summary--pending'}`} data-testid="dt-summary">
          <div className="dt-summary-item">
            <div className="dt-summary-num">1×</div>
            <div className="dt-summary-lbl">{T.summary.stored}</div>
          </div>
          <div className="dt-summary-divider" />
          <div className="dt-summary-item">
            <div className="dt-summary-num">4×</div>
            <div className="dt-summary-lbl">{T.summary.cycles}</div>
          </div>
          <div className="dt-summary-divider" />
          <div className="dt-summary-item">
            <div className="dt-summary-num">12</div>
            <div className="dt-summary-lbl">{T.summary.months}</div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
